import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { FileEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { createGroupLink, extractPlainText, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type FileDeletedEvent = Extract<FileEvents, { eventTypeName: "FileEvent.Deleted" }>;
const renderFileDeleted = (props: RendererProps<FileDeletedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { group } = context;
    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.fileDeletedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.fileDeletedDescription"
                values={{
                    entityId: event.entityId,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type FileRenamedEvent = Extract<FileEvents, { eventTypeName: "FileEvent.Renamed" }>;
const renderFileRenamed = (props: RendererProps<FileRenamedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { group } = context;
    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.fileRenamedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.fileRenamedDescription"
                values={{
                    entityId: event.entityId,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

export const renderFileEventContent = (event: FileEvents, context: EventContext, theme: Theme): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "FileEvent.Deleted":
            return renderFileDeleted({ event, context, theme });
        case "FileEvent.Renamed":
            return renderFileRenamed({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
